
import moment from 'moment';
import login from '../../modules/auth/application/login';
import AuthError from '../../modules/auth/domain/AuthError';
import OrderNotFoundError from '../../modules/auth/domain/OrderNotFoundError';
import createAuthApiRepository from '../../modules/auth/infrastructure/AuthApiRepository';
import orderDetail from '../../libs/mixins/orderDetail';
import saveLoginData from '../../libs/utils/authUtils';

const DEFAULT_OTP_LENGTH = 6;
const DEFAULT_MAX_RESENDS = 3;
const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;
const PHONE_NUMBER_LENGTH = 10;
const SERVICE_UNAVAILABLE_MESSAGE = 'No pudimos comunicarnos con el servicio en este momento. Intenta de nuevo más tarde.';
const OTP_CHANNEL_EMAIL = 'EMAIL';
const OTP_CHANNEL_SMS = 'SMS';

export default {
  mixins: [orderDetail],

  data() {
    return {
      email: '',
      phone: '',
      otp: '',
      channel: OTP_CHANNEL_EMAIL,
      challengeId: '',
      error: null,
      isLoading: false,
      showTutorial: 0,
      showNoticePrivacy: false,
      step: 'request',
      otpLength: DEFAULT_OTP_LENGTH,
      resendTimer: 0,
      resendCount: 0,
      maxResends: DEFAULT_MAX_RESENDS,
      timerId: null,
    };
  },

  computed: {
    bannerColor() {
      if (this.error instanceof OrderNotFoundError) {
        return 'purple-custom';
      }

      return 'negative';
    },

    isOtpStep() {
      return this.step === 'otp';
    },

    showPhoneInput() {
      return this.channel === OTP_CHANNEL_SMS;
    },

    channelOptions() {
      return [
        { label: 'Correo electrónico', value: OTP_CHANNEL_EMAIL },
        { label: 'SMS', value: OTP_CHANNEL_SMS },
      ];
    },

    resendButtonLabel() {
      return `Reenviar código (${this.resendCount}/${this.maxResends})`;
    },

    errorTitle() {
      if (!this.error) {
        return '';
      }

      if (this.error instanceof OrderNotFoundError) {
        return 'No pudimos iniciar tu sesión con ese correo electrónico';
      }

      const message = this.normalizedErrorMessage;

      if (message.includes('correo electrónico con formato válido')
        || message.includes('ingresa un correo electrónico')) {
        return 'Revisa el correo electrónico';
      }

      if (message.includes('número telefónico') || message.includes('teléfono')) {
        return 'Revisa el número telefónico';
      }

      if (message.includes('otp inválido') || message.includes('intentos restantes')) {
        return 'El código no coincide';
      }

      if (message.includes('expiró')) {
        return 'El código ya venció';
      }

      if (message.includes('bloqueado')) {
        return 'Se bloqueó temporalmente la validación';
      }

      if (message.includes('debes esperar')
        || message.includes('límite máximo de reenvíos')
        || message.includes('demasiadas solicitudes')) {
        return 'Espera antes de solicitar otro código';
      }

      if (message.includes('no pudimos comunicarnos con el servicio')) {
        return 'No pudimos completar tu acceso';
      }

      return 'No pudimos completar tu acceso';
    },

    errorActions() {
      if (!this.error) {
        return [];
      }

      if (this.error instanceof OrderNotFoundError) {
        return [
          'Verifica que el correo electrónico sea el mismo que registraste en la concesionaria.',
          'Si tu correo cambió, solicita a tu asesor que lo actualice antes de intentar ingresar de nuevo.',
          'Si necesitas apoyo inmediato, pide al personal de la agencia que confirme tus datos.',
        ];
      }

      const message = this.normalizedErrorMessage;

      if (message.includes('correo electrónico con formato válido')
        || message.includes('ingresa un correo electrónico')) {
        return [
          'Revisa que el correo esté escrito completo, por ejemplo: nombre@dominio.com.',
          'Evita espacios al inicio o al final antes de volver a intentarlo.',
        ];
      }

      if (message.includes('número telefónico') || message.includes('teléfono')) {
        return [
          'Captura 10 dígitos seguidos, sin espacios ni caracteres especiales.',
          'Si no cuentas con un número disponible, cambia el canal a correo electrónico.',
        ];
      }

      if (message.includes('otp inválido') || message.includes('intentos restantes')) {
        return [
          'Revisa el último código recibido por correo electrónico o SMS.',
          'Si ya solicitaste un reenvío, usa únicamente el código más reciente.',
          'Si el problema continúa, solicita un nuevo código desde esta misma pantalla.',
        ];
      }

      if (message.includes('expiró')) {
        return [
          'Solicita un nuevo código para continuar con el acceso.',
          'Usa el código en cuanto lo recibas para evitar que vuelva a vencer.',
        ];
      }

      if (message.includes('bloqueado')) {
        return [
          'Selecciona "Cambiar datos" y solicita un código nuevo para reiniciar el proceso.',
          'Si el bloqueo persiste, solicita apoyo al personal de la agencia.',
        ];
      }

      if (message.includes('debes esperar')
        || message.includes('límite máximo de reenvíos')
        || message.includes('demasiadas solicitudes')) {
        return [
          'Espera el tiempo indicado antes de volver a solicitar otro código.',
          'Mientras tanto, revisa tu correo electrónico o SMS por si ya recibiste el código vigente.',
        ];
      }

      return this.getGenericErrorActions();
    },

    normalizedErrorMessage() {
      if (!this.error) {
        return '';
      }

      return this.getDisplayErrorMessage(this.error).toLowerCase();
    },

    errorMessage() {
      if (!this.error) {
        return '';
      }

      if (this.shouldHideErrorMessage(this.error)) {
        return '';
      }

      return this.getDisplayErrorMessage(this.error);
    },
  },

  watch: {
    channel() {
      this.error = null;
    },
  },

  beforeDestroy() {
    this.clearResendCountdown();
  },

  methods: {
    updateEmail(value) {
      this.email = String(value || '');
      this.error = null;
    },

    getGenericErrorActions() {
      return [
        'Verifica que el correo electrónico capturado sea correcto.',
        'Intenta nuevamente en unos minutos para descartar un problema temporal.',
        'Si el problema continúa, solicita apoyo del personal de la agencia para revisar tus datos.',
      ];
    },

    isTechnicalErrorMessage(message) {
      const normalizedMessage = String(message || '').toLowerCase();

      return normalizedMessage.includes('can\'t access property')
        || normalizedMessage.includes('cannot read properties of undefined')
        || normalizedMessage.includes('e.response is undefined')
        || normalizedMessage.includes('(void 0) is undefined');
    },

    shouldHideErrorMessage(error) {
      const rawMessage = String(error?.message || '').trim();
      const displayMessage = this.getDisplayErrorMessage(error);

      return this.isTechnicalErrorMessage(rawMessage)
        || displayMessage === SERVICE_UNAVAILABLE_MESSAGE;
    },

    getDisplayErrorMessage(error) {
      const rawMessage = String(error?.message || '').trim();
      const normalizedMessage = rawMessage.toLowerCase();

      if (!rawMessage) {
        return SERVICE_UNAVAILABLE_MESSAGE;
      }

      if (this.isTechnicalErrorMessage(normalizedMessage)
        || normalizedMessage.includes('network error')
        || normalizedMessage.includes('failed to fetch')
        || normalizedMessage.includes('load failed')
        || normalizedMessage.includes('timeout')) {
        return SERVICE_UNAVAILABLE_MESSAGE;
      }

      return rawMessage;
    },

    updatePhone(value) {
      const inputValue = String(value || '');

      this.error = null;

      if (!/^[0-9]*$/.test(inputValue)) {
        this.error = new AuthError('El teléfono debe contener solo números.');
      }

      this.phone = inputValue.replace(/[^0-9]/g, '').slice(0, PHONE_NUMBER_LENGTH);
    },

    updateOtp(value) {
      this.error = null;
      this.otp = String(value || '').replace(/[^0-9]/g, '').slice(0, this.otpLength);
    },

    resetOtpFlow() {
      this.step = 'request';
      this.challengeId = '';
      this.otp = '';
      this.resendTimer = 0;
      this.resendCount = 0;
      this.maxResends = DEFAULT_MAX_RESENDS;
      this.error = null;
      this.clearResendCountdown();
    },

    clearResendCountdown() {
      if (this.timerId !== null) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    },

    startResendCountdown(seconds) {
      this.clearResendCountdown();
      this.resendTimer = seconds;

      if (this.resendTimer <= 0) {
        return;
      }

      this.timerId = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer -= 1;
        }

        if (this.resendTimer <= 0) {
          this.clearResendCountdown();
        }
      }, 1000);
    },

    hasValidEmailFormat() {
      const emailValue = String(this.email || '').trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
    },

    hasValidPhoneFormat() {
      if (!this.showPhoneInput) {
        return true;
      }

      return new RegExp(`^[0-9]{${PHONE_NUMBER_LENGTH}}$`).test(String(this.phone || ''));
    },

    validateRequestOtpInput() {
      const emailValue = String(this.email || '').trim();

      if (!emailValue) {
        this.error = new AuthError('Ingresa un correo electrónico.');
        return false;
      }

      if (!this.hasValidEmailFormat()) {
        this.error = new AuthError('Ingresa un correo electrónico con formato válido.');
        return false;
      }

      this.email = emailValue;

      if (!this.showPhoneInput) {
        return true;
      }

      if (!this.phone) {
        this.error = new AuthError('Ingresa un número telefónico de 10 dígitos.');
        return false;
      }

      if (!this.hasValidPhoneFormat()) {
        this.error = new AuthError('Ingresa un número telefónico válido de 10 dígitos (solo números).');
        return false;
      }

      return true;
    },

    async requestOtp(isResend = false) {
      this.error = null;

      if (!this.validateRequestOtpInput()) {
        return;
      }

      this.isLoading = true;

      try {
        const repository = createAuthApiRepository();

        const data = await repository.requestOtp({
          email: this.email,
          orderHigherIdExt: this.$session.get('superOrderId'),
          channel: this.channel,
          phone: this.showPhoneInput ? this.phone : null,
          challengeId: isResend ? this.challengeId : null,
        });

        this.step = 'otp';
        this.challengeId = data.challengeId;
        this.otpLength = data.otpLength || DEFAULT_OTP_LENGTH;
        this.resendCount = data.resendCount || 0;
        this.maxResends = data.maxResends || DEFAULT_MAX_RESENDS;
        this.otp = '';
        this.startResendCountdown(data.cooldownSeconds || DEFAULT_RESEND_COOLDOWN_SECONDS);
      } catch (error) {
        this.error = error;
      } finally {
        this.isLoading = false;
      }
    },

    async verifyOtp() {
      this.isLoading = true;
      this.error = null;

      try {
        const repository = createAuthApiRepository();

        const data = await repository.verifyOtp({
          email: this.email,
          orderHigherIdExt: this.$session.get('superOrderId'),
          challengeId: this.challengeId,
          otp: this.otp,
        });

        if (data && data.token) {
          await this.completeLoginFlow(data);
          return;
        }

        await this.login();
      } catch (error) {
        this.error = error;
      } finally {
        this.isLoading = false;
      }
    },

    async login() {
      const repository = createAuthApiRepository();

      const data = await login(repository, {
        email: this.email,
        orderHigherIdExt: this.$session.get('superOrderId'),
      });

      await this.completeLoginFlow(data);
    },

    async completeLoginFlow(data) {
      this.saveTokenInSession(data.token, data.expires_in);

      const { showTutorial, showNoticePrivacy } = saveLoginData(this.$session, data, this.email);

      this.showTutorial = showTutorial;
      this.showNoticePrivacy = showNoticePrivacy;

      await this.getOrderDetail();
    },

    saveTokenInSession(token, expiresIn) {
      localStorage.setItem('token', token);
      this.$session.set('token', token);
      this.$session.set('expire_date', moment().add(expiresIn, 'seconds').format('YYYY-MM-DD HH:mm'));
    },
  },
};
